import { apiService } from './api';

// Types
export interface CreateAuditRequest {
  website_url: string;
  organization: number;
  project: number;
}

export interface Audit {
  id: number;
  audit_id: string;
  website_url: string;
  website_name?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  organization: number;
  project: number;
  created_at: string;
  updated_at: string;
  audit_data?: any;
}

export interface Issue {
  id: number;
  title: string;
  description: string;
  severity: 'critical' | 'high' | 'medium' | 'low' | 'info';
  category: string;
  url: string;
  element: string;
  recommendation: string;
  status: 'pending' | 'fixed' | 'ignored';
  created_at: string;
  updated_at?: string;
}

export interface AuditProgress {
  audit_id: number;
  status: string;
  progress_percentage: number;
  updated_at: string;
}

export interface AuditStats {
  total_audits: number;
  completed_audits: number;
  failed_audits: number;
  total_issues: number;
  critical_issues: number;
  high_issues: number;
  medium_issues: number;
  low_issues: number;
  info_issues: number;
}

export interface IssueCategory {
  id: number;
  name: string;
  description: string;
  color: string;
}

// API Functions
export const auditService = {
  // Create a new audit
  createAudit: async (data: CreateAuditRequest): Promise<Audit> => {
    const response = await apiService.post('/audit/', data);
    return response.data as unknown as Audit;
  },

  // Get all audits with optional filters
  getAudits: async (params?: {
    organization_id?: number;
    project_id?: number;
    status?: string;
  }): Promise<{ results: Audit[]; count: number }> => {
    const response = await apiService.get('/audit/', { params });
    return response.data as unknown as { results: Audit[]; count: number };
  },

  // Get specific audit by audit_id
  getAudit: async (auditId: string): Promise<Audit> => {
    const response = await apiService.get(`/audit/${auditId}/`);
    return response.data as unknown as Audit;
  },

  // Check audit progress
  getAuditProgress: async (auditId: string): Promise<AuditProgress> => {
    const response = await apiService.get(`/audit/${auditId}/progress/`);
    return response.data as unknown as AuditProgress;
  },

  // Update audit
  updateAudit: async (auditId: string, data: Partial<Audit>): Promise<Audit> => {
    const response = await apiService.patch(`/audit/${auditId}/`, data);
    return response.data as unknown as Audit;
  },

  // Delete audit
  deleteAudit: async (auditId: string): Promise<void> => {
    await apiService.delete(`/audit/${auditId}/`);
  },

  // Get issues for an audit
  getAuditIssues: async (auditId: string, params?: {
    severity?: string;
    status?: string;
    category_id?: number;
  }): Promise<{ results: Issue[]; count: number }> => {
    const response = await apiService.get(`/audit/${auditId}/issues/`, { params });
    return response.data as unknown as { results: Issue[]; count: number };
  },

  // Update issue status
  updateIssueStatus: async (issueId: number, data: {
    status: 'pending' | 'fixed' | 'ignored';
    recommendation?: string;
  }): Promise<Issue> => {
    const response = await apiService.patch(`/audit/issues/${issueId}/`, data);
    return response.data as unknown as Issue;
  },

  // Get audit statistics
  getAuditStats: async (organizationId?: number): Promise<AuditStats> => {
    const params = organizationId ? { organization_id: organizationId } : {};
    const response = await apiService.get('/audit/stats/', { params });
    return response.data as unknown as AuditStats;
  },

  // Get issue categories
  getIssueCategories: async (): Promise<IssueCategory[]> => {
    const response = await apiService.get('/audit/categories/');
    return response.data as unknown as IssueCategory[];
  },

  // Fix all issues for an audit (AI-powered)
  fixAllIssues: async (auditId: string): Promise<{ success: boolean; fixed_count: number }> => {
    const response = await apiService.post(`/audit/${auditId}/fix-all/`);
    return response.data as unknown as { success: boolean; fixed_count: number };
  },

  // Fix single issue (AI-powered)
  fixSingleIssue: async (issueId: number): Promise<{ success: boolean; fixed: boolean }> => {
    const response = await apiService.post(`/audit/issues/${issueId}/fix/`);
    return response.data as unknown as { success: boolean; fixed: boolean };
  },

  // Get audit webhook data (for internal use)
  getWebhookData: async (auditId: string): Promise<any> => {
    const response = await apiService.get(`/audit/${auditId}/webhook/`);
    return response.data;
  }
};

export default auditService;
